import {Component, OnInit} from '@angular/core';
import {NgIf} from "@angular/common";
import * as ApexCharts from "apexcharts";
import {AuthService} from "../../services/auth.service";
import {dashboardInfo} from "../../state/dashboard/dashboard.actions";
import {selectDashboardInfoFailure, selectDashboardInfoSuccess} from "../../state/dashboard/dashboard.selectors";
import {takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {AppState} from "../../state/app.state";
import {Subject} from "rxjs";

@Component({
  selector: 'app-icp-optimization-chart',
  standalone: true,
  imports: [
    NgIf
  ],
  templateUrl: './icp-optimization-chart.component.html',
  styleUrl: './icp-optimization-chart.component.scss'
})
export class IcpOptimizationChartComponent implements OnInit {

  dashboard: any = null;
  dataReady = false;
  profileCompletion: number = 0;

  // user: any = null;

  private destroy$ = new Subject<void>();
  private chartsInitialized = false;

  profileChart: ApexCharts | null = null;

  public profileCompletionColor: string = '#4CAF50'; // Default green

  constructor(
    private store: Store<AppState>,
    private authService: AuthService
  ) {
    //
  }

  ngOnInit(): void {
    // console.log('[icp-optimization-chart.component.ts] ngOnInit')

    this.getDashboardInfo();

    /*
    this.authService.authUser().subscribe(user => {
      this.user = user;
      console.log('user', user)
    });
    */

  }

  ngAfterViewInit(): void {
    // console.log('[dashboard.component.ts] ngAfterViewInit')

    // This ensures that charts are initialized after view is fully rendered
    if (this.dataReady && !this.chartsInitialized) {
      this.initCharts();
    }

    // Enable animations after component is fully rendered
    setTimeout(() => {
      const sidebarEl = document.querySelector('.sidebar-container');
      if (sidebarEl) {
        sidebarEl.classList.add('animation-enabled');
      }
    }, 100);
  }

  getDashboardInfo() {
    // Dispatch the action to fetch dashboard info
    this.store.dispatch(dashboardInfo());

    // Listen for success response
    this.store
      .select(selectDashboardInfoSuccess)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result && result.dashboard) {
            this.dashboard = result.dashboard;

            this.profileCompletion = result.dashboard.profile_completion

            this.dataReady = true;

            // Initialize charts after ensuring DOM elements are available
            setTimeout(() => this.initCharts(), 0);
          }
        },
        error: (error) => {
          console.error('Error loading dashboard info:', error);
        },
      });

    // Handle failure response
    this.store
      .select(selectDashboardInfoFailure)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (error) => {
          if (error) {
            console.error('Failed to load dashboard info:', error);
            // Optional: Show a notification or take other appropriate actions
          }
        },
      });
  }

  initCharts() {
    // Prevent multiple initializations
    if (this.chartsInitialized || !this.dataReady) {
      return;
    }

    // Use setTimeout to delay chart initialization until after the next render cycle
    setTimeout(() => {
      // Clean up any existing charts before initializing
      this.cleanupCharts();

      // Initialize charts
      this.initProfileCompletedChart();

      // Mark charts as initialized
      this.chartsInitialized = true;
    }, 50); // Short delay to ensure DOM is ready
  }

  initProfileCompletedChart() {
    const chartContainer = document.querySelector('#profile-completed-chart');
    if (!chartContainer) {
      console.error('Chart container #profile-completed-chart does not exist');
      return;
    }

    const sanitizedValue = Math.max(0, parseFloat(this.dashboard?.profile_completion) || 0);

    // Determine the color and apply a data attribute to the container
    if (sanitizedValue < 50) {
      this.profileCompletionColor = '#FF3A2D'; // Red for scores 0-49
      chartContainer.setAttribute('data-completion-level', 'low');
    } else if (sanitizedValue < 100) {
      this.profileCompletionColor = '#FC8226'; // Orange for scores 50-99
      chartContainer.setAttribute('data-completion-level', 'medium');
    } else {
      this.profileCompletionColor = '#4CAF50'; // Green for score 100
      chartContainer.setAttribute('data-completion-level', 'high');
    }

    const options = {
      series: [sanitizedValue],
      chart: {
        type: 'radialBar',
        height: 90, // Fixed height
        width: 90,  // Fixed width
      },
      plotOptions: {
        radialBar: {
          hollow: {
            size: '50%', // Adjust the size of the hollow center
          },
          track: {
            background: '#E3F3FF',
          },
          dataLabels: {
            name: {
              show: false, // Hide the "name" label
            },
            value: {
              show: true, // Show the value
              offsetY: 6, // Adjust the vertical position
              formatter: (val: number) => `${val}%`, // Format the value
            },
          },
        },
      },
      colors: [this.profileCompletionColor], // Use the dynamically determined color
    };

    // Ensure only one chart instance is active
    if (this.profileChart) {
      this.profileChart.destroy();
    }

    this.profileChart = new ApexCharts(chartContainer, options);
    this.profileChart.render();
  }

  cleanupCharts(): void {
    if (this.profileChart) {
      this.profileChart.destroy();
      this.profileChart = null;
    }

    const chartContainers = ['#profile-completed-chart'];
    chartContainers.forEach((selector) => {
      const container = document.querySelector(selector);
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up charts and subscriptions
    this.chartsInitialized = false;
    this.destroy$.next();
    this.destroy$.complete();

    this.cleanupCharts();
  }

}
